import { API_BASE_URL, API_ROOT_URL, BACKEND_URL } from '@/lib/constants/api';
import { apiFetch } from '@/lib/utils/apiFetch';

export interface DocumentPrivacyStatus {
  status:
    | 'public'
    | 'private'
    | 'private_requests'
    | 'shared'
    | 'shared_to'
    | 'shared_by';
  requests: number;
  approvedRequests: number;
  company?: { name: string } | null;
}

export interface DocumentPermission {
  status: 'requested' | 'none';
  data: null | { documentId: string };
}

export interface Document {
  id: string;
  name: string;
  fileUrl: string | null;
  type: string | null;
  isPublic: boolean;
  updatedAt: string | null;
  updatedByUserName: string | null;
  updatedByCompanyName: string | null;
  privacyStatus: DocumentPrivacyStatus | null;
  permission?: DocumentPermission | null;
  // Present when isWithNetworkDocuments=true
  companyId?: string | null;
  companyName?: string | null;
  requestCompanyFacilityProcesses?: string | null;
}

export interface NetworkDocument extends Document {
  companyId: string;
  companyName: string | null;
  requestCompanyFacilityProcesses: string | null;
}

export interface DocumentsResponse {
  data: Document[];
  meta: { totalCount: number; pageSize: number; page: number };
}

export interface NetworkDocumentsResponse {
  data: NetworkDocument[];
  meta: { totalCount: number; pageSize: number; page: number };
}

export interface DocumentType {
  id: string;
  title: string;
}

export type DocumentTypesResponse = DocumentType[];

export interface DocumentPermissionEntry {
  sharedWithCompanyId: string;
  sharedWithCompanyName: string | null;
  requestedAt: string | null;
  approvedAt: string | null;
}

export interface DocumentPermissionsResponse {
  id: string;
  name: string;
  permissions: DocumentPermissionEntry[];
}

export interface BulkCreateDocumentItem {
  name: string;
  documentTypeId: string;
  fileId: string;
  isPublic: boolean;
}

export interface SharedCompanyDocument {
  id: string;
  name: string;
  fileId?: string | null;
  fileUrl?: string | null;
  createdAt?: string | null;
}

// Pure RPC — takes a pre-built query string, returns raw response

export async function fetchSharedCompanyDocuments(
  companyId: string,
  documentType: string,
): Promise<{ data: SharedCompanyDocument[] }> {
  const qs = new URLSearchParams({ companyId, documentType }).toString();
  return apiFetch<{ data: SharedCompanyDocument[] }>(
    `/api/company-documents/shared?${qs}`,
    {},
    API_ROOT_URL,
  );
}

export async function fetchDocuments(qs: string): Promise<DocumentsResponse> {
  return apiFetch<DocumentsResponse>(
    `/v3/company-documents?${qs}`,
    {},
    API_BASE_URL,
  );
}

export async function fetchNetworkDocuments(
  qs: string,
): Promise<NetworkDocumentsResponse> {
  return apiFetch<NetworkDocumentsResponse>(
    `/v3/company-documents?${qs}`,
    {},
    API_BASE_URL,
  );
}

export async function fetchDocumentTypes(
  qs: string,
): Promise<DocumentTypesResponse> {
  return apiFetch<DocumentTypesResponse>(
    `/v3/document-types/drop-down?${qs}`,
    {},
    API_BASE_URL,
  );
}

// Canonical `/document-types` list route — modern TS API. Prefer this over the
// legacy `fetchDocumentTypes` above in new code.

export interface DocumentTypeListItem {
  id: string;
  title: string;
  documentClass: 'TRANSACTIONAL' | 'COMPANY' | 'ALL';
  isDeprecated: boolean;
  impactAreaId: string | null;
}

export interface DocumentTypesListResponse {
  meta: { count: number; page: number; pageSize: number; totalCount: number };
  data: DocumentTypeListItem[];
}

export async function fetchDocumentTypesList(
  qs: string,
): Promise<DocumentTypesListResponse> {
  return apiFetch<DocumentTypesListResponse>(
    `/document-types?${qs}`,
    {},
    BACKEND_URL,
  );
}

export async function fetchDocumentPermissions(
  id: string,
): Promise<DocumentPermissionsResponse> {
  return apiFetch<DocumentPermissionsResponse>(
    `/v3/company-documents/${id}`,
    {},
    API_BASE_URL,
  );
}

export async function patchDocument(
  id: string,
  body: { name?: string; documentTypeId?: string },
): Promise<void> {
  await apiFetch<void>(
    `/v3/company-documents/${id}`,
    { method: 'PATCH', body: JSON.stringify(body) },
    API_BASE_URL,
  );
}

export async function bulkArchiveDocuments(ids: string[]): Promise<void> {
  await apiFetch<void>(
    '/v3/company-documents/bulk-archive',
    { method: 'PATCH', body: JSON.stringify(ids) },
    API_BASE_URL,
  );
}

export async function bulkCreateDocuments(payload: {
  companyId: string;
  documents: BulkCreateDocumentItem[];
}): Promise<void> {
  await apiFetch<void>(
    '/v3/company-documents/bulk-create',
    { method: 'POST', body: JSON.stringify(payload) },
    API_BASE_URL,
  );
}

export async function downloadDocuments(
  ids: string[],
): Promise<{ data: { id: string; url: string } }> {
  return apiFetch<{ data: { id: string; url: string } }>(
    '/company-documents/download',
    { method: 'POST', body: JSON.stringify(ids) },
    BACKEND_URL,
  );
}

export async function shareDocument(
  id: string,
  companyIds: string[],
): Promise<void> {
  await apiFetch<void>(
    `/v3/company-documents/${id}/share`,
    { method: 'PUT', body: JSON.stringify(companyIds) },
    API_BASE_URL,
  );
}

export async function makeDocumentPublic(id: string): Promise<void> {
  await apiFetch<void>(
    '/v3/company-documents/make-public',
    { method: 'PATCH', body: JSON.stringify({ id, confirmation: 'public' }) },
    API_BASE_URL,
  );
}

export async function requestDocumentAccess(id: string): Promise<void> {
  await apiFetch<void>(
    `/v3/company-documents/${id}/request`,
    { method: 'POST', body: JSON.stringify({}) },
    API_BASE_URL,
  );
}

export async function cancelDocumentAccessRequest(id: string): Promise<void> {
  await apiFetch<void>(
    `/v3/company-documents/${id}/cancel-request`,
    { method: 'POST', body: JSON.stringify({}) },
    API_BASE_URL,
  );
}
